import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import exportService from "../features/exports/exportService";
import organisationService from "../features/organisation/organisationService";
import { useAccessControl } from "../context/AccessControlContext";

/**
 * The rankings as a spreadsheet.
 *
 * One export at a time: the national ranking, or one organisation's published
 * standings on one of its gender tabs. Both are already public - the national
 * ranking backs the home page and an organisation's standings back its own
 * page - so this hands an admin nothing new, only a file.
 *
 * The numbers are the pages' numbers by construction: the server calls the same
 * service functions the pages do, an organisation's export under that page's own
 * cache key.
 */

// The order the public page offers its tabs in, copied from OrgRankingPage's
// GENDER_ORDER so this form and that page cannot disagree.
const GENDER_ORDER = ["Men", "Women", "All"];

/**
 * What the server will accept for this organisation: its configured tabs, or a
 * single combined tab when it has none. Deliberately the server's fallback
 * (["All"]) and not the public page's all-three one - offering a tab the server
 * refuses would be a 400 the admin could not explain.
 */
const tabsFor = (org) => {
  const configured = Array.isArray(org?.gender_tabs)
    ? org.gender_tabs.filter(Boolean)
    : [];
  const tabs = configured.length ? configured : ["All"];
  return GENDER_ORDER.filter((tab) => tabs.includes(tab));
};

const todayInSast = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Johannesburg" });

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "organisation";

/**
 * A failed export answers JSON, but axios hands the body over as a Blob because
 * this request asks for one - so the message has to be read out of it rather
 * than read off err.response.data.
 */
const messageFor = async (err) => {
  const data = err.response?.data;

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch {
      // Not JSON - fall through to the generic message.
    }
  }

  return data?.message || err.message || "Failed to export the rankings";
};

export default function Exports() {
  const { isAdmin, isLoading: accessLoading } = useAccessControl();

  const [organisations, setOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [scope, setScope] = useState("national");
  const [organisationId, setOrganisationId] = useState("");
  const [gender, setGender] = useState("");
  const [date, setDate] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await organisationService.listOrganisations();
      const rows = response.data?.organisations || [];
      // The service returns deactivated organisations and the server refuses
      // them, so they are not offered here.
      setOrganisations(rows.filter((org) => org.is_active !== false));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load organisations",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const selectedOrg = useMemo(
    () =>
      organisations.find((org) => String(org.id) === organisationId) || null,
    [organisations, organisationId],
  );

  const tabs = selectedOrg ? tabsFor(selectedOrg) : [];
  // Derived rather than stored, so there is no stale tab to reset when the
  // chosen organisation changes: an unset or unavailable value falls back to
  // the tab the server would itself default to.
  const effectiveGender = tabs.includes(gender) ? gender : tabs[0] || "";

  const filename = useMemo(() => {
    const asOf = date || todayInSast();
    if (scope === "national") return `pba-national-ranking-${asOf}.xlsx`;

    const slug = selectedOrg?.slug || slugify(selectedOrg?.name);
    return `${slug}-${effectiveGender.toLowerCase()}-ranking-${asOf}.xlsx`;
  }, [scope, date, selectedOrg, effectiveGender]);

  const download = async () => {
    setIsDownloading(true);
    setError("");
    setNotice("");

    try {
      const params = { scope };
      if (scope === "organisation") {
        params.organisationId = organisationId;
        if (effectiveGender) params.gender = effectiveGender;
      }
      if (date) params.date = date;

      const response = await exportService.downloadRankingsExport(params);

      // An object URL rather than a data: string - the file runs to thousands
      // of rows - and the anchor has to be in the document for Firefox.
      const url = URL.createObjectURL(response.data);
      try {
        const element = document.createElement("a");
        element.href = url;
        element.download = filename;
        element.style.display = "none";
        document.body.appendChild(element);
        element.click();
        element.remove();
      } finally {
        URL.revokeObjectURL(url);
      }

      setNotice(`Downloaded ${filename}`);
    } catch (err) {
      setError(await messageFor(err));
    } finally {
      setIsDownloading(false);
    }
  };

  if (accessLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Access Denied: Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  const canDownload =
    !isDownloading && (scope === "national" || Boolean(organisationId));

  return (
    <Box sx={{ py: 2, maxWidth: 640 }}>
      <Typography variant="h5" gutterBottom>
        Exports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Download a ranking as an Excel workbook, with one row per player: rank,
        player, BSA number and points. The numbers are the ones the ranking
        pages show.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={2.5}>
          <FormControl fullWidth>
            <InputLabel id="export-scope">Ranking</InputLabel>
            <Select
              labelId="export-scope"
              label="Ranking"
              value={scope}
              onChange={(event) => {
                setScope(event.target.value);
                setNotice("");
              }}
            >
              <MenuItem value="national">National ranking</MenuItem>
              <MenuItem value="organisation">
                An organisation's ranking
              </MenuItem>
            </Select>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              {scope === "national"
                ? "Every ranked player. The national ranking has no gender tabs."
                : "One organisation's published standings, as its own page shows them."}
            </Typography>
          </FormControl>

          {scope === "organisation" && (
            <>
              <FormControl fullWidth>
                <InputLabel id="export-org">Organisation</InputLabel>
                <Select
                  labelId="export-org"
                  label="Organisation"
                  value={organisationId}
                  onChange={(event) => {
                    setOrganisationId(event.target.value);
                    setNotice("");
                  }}
                >
                  <MenuItem value="">Choose an organisation</MenuItem>
                  {organisations.map((org) => (
                    <MenuItem key={org.id} value={String(org.id)}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Only when there is a choice: an organisation publishing one
                  combined ranking has no tab row on its page either. */}
              {tabs.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel id="export-gender">Ranking tab</InputLabel>
                  <Select
                    labelId="export-gender"
                    label="Ranking tab"
                    value={effectiveGender}
                    onChange={(event) => {
                      setGender(event.target.value);
                      setNotice("");
                    }}
                  >
                    {tabs.map((tab) => (
                      <MenuItem key={tab} value={tab}>
                        {tab}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <TextField
              label="As of date"
              type="date"
              size="small"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for the current ranking"
            />
            {date && (
              <Button
                variant="text"
                onClick={() => setDate("")}
                sx={{ mt: 0.25 }}
              >
                Clear
              </Button>
            )}
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={download}
              disabled={!canDownload}
            >
              {isDownloading ? "Preparing..." : "Download .xlsx"}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {canDownload ? filename : "Choose an organisation to export."}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
